import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
  RetryContext,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { TOOL_LIBRARY_BASE_API_URL } from '@tfs/tool-library-tokens';

import { NotificationEvent } from './notification-events';

const HUB_PATH = '/hubs/notifications';
// Server-side counterpart: Tfs.Common.Application.Hub.Realtime.RealtimeNotificationService
// invokes the `notification` client method. SignalR lower-cases method names internally,
// so casing here only matters for readability.
const HUB_CLIENT_METHOD = 'notification';

const RECENT_EVENTS_STORAGE_KEY = 'tfs.notifications.recent';
const MAX_RECENT_EVENTS = 200;

/**
 * Reconnect forever, ramping up to 30s. Mirrors `DefaultReconnectPolicy`'s ramp for the
 * first few attempts so transient blips recover quickly, then holds at 30s indefinitely.
 * Bail-out only happens when consumers call `stop()`.
 */
class ForeverReconnectPolicy implements IRetryPolicy {
  private static readonly RAMP_MS = [0, 2_000, 5_000, 10_000, 30_000];

  nextRetryDelayInMilliseconds(ctx: RetryContext): number {
    return ForeverReconnectPolicy.RAMP_MS[ctx.previousRetryCount] ?? 30_000;
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationsHubClient {
  private readonly toolLibraryBaseApiUrl = inject(TOOL_LIBRARY_BASE_API_URL);
  private readonly destroyRef = inject(DestroyRef);

  private connection?: HubConnection;
  private startPromise?: Promise<void>;

  private readonly notificationsSubject = new Subject<NotificationEvent>();
  private readonly connectionStateSignal = signal<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  private readonly recentEventsSubject = new BehaviorSubject<NotificationEvent[]>(
    loadRecentEvents()
  );

  /**
   * Live stream of every received envelope. Consumers narrow with their own type-guard,
   * e.g. `notifications$.pipe(filter(isToolImportJobStatusChangeEvent))`.
   */
  readonly notifications$: Observable<NotificationEvent> = this.notificationsSubject.asObservable();

  readonly connectionState: Signal<HubConnectionState> = this.connectionStateSignal.asReadonly();

  /**
   * Rolling buffer of received envelopes (newest first), capped at {@link MAX_RECENT_EVENTS}
   * and persisted to sessionStorage so the dev panel sees events even if it wasn't
   * mounted when they arrived. Cleared on tab close.
   */
  readonly recentEvents$: Observable<NotificationEvent[]> = this.recentEventsSubject.asObservable();

  constructor() {
    this.destroyRef.onDestroy(() => {
      void this.stop();
    });
  }

  start(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${this.toolLibraryBaseApiUrl}${HUB_PATH}`)
      .withAutomaticReconnect(new ForeverReconnectPolicy())
      .configureLogging(LogLevel.Warning)
      .build();

    this.wireHubCallbacks(connection);

    this.connection = connection;
    this.connectionStateSignal.set(HubConnectionState.Connecting);

    const startPromise = connection
      .start()
      .then(() => {
        this.connectionStateSignal.set(HubConnectionState.Connected);
      })
      .catch((error: unknown) => {
        // Realtime is best-effort; consumers should resync via REST.
        this.resetToDisconnected();
        console.error('[NotificationsHubClient] Failed to connect to SignalR hub:', error);
      });

    this.startPromise = startPromise;
    return startPromise;
  }

  async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = undefined;
    this.startPromise = undefined;

    if (!connection) {
      return;
    }

    try {
      await connection.stop();
    } catch (error) {
      console.warn('[NotificationsHubClient] Error stopping SignalR connection:', error);
    }

    this.connectionStateSignal.set(HubConnectionState.Disconnected);
  }

  clearRecentEvents(): void {
    this.recentEventsSubject.next([]);
    try {
      sessionStorage.removeItem(RECENT_EVENTS_STORAGE_KEY);
    } catch {
      // sessionStorage may be unavailable (private mode etc.) — ignore.
    }
  }

  private wireHubCallbacks(connection: HubConnection): void {
    connection.on(HUB_CLIENT_METHOD, (payload: NotificationEvent) => {
      this.notificationsSubject.next(payload);
      this.recordRecentEvent(payload);
    });

    connection.onreconnecting(() => {
      this.connectionStateSignal.set(HubConnectionState.Reconnecting);
    });
    connection.onreconnected(() => {
      this.connectionStateSignal.set(HubConnectionState.Connected);
    });
    // With ForeverReconnectPolicy this only fires on explicit stop(); cleanup defensively.
    connection.onclose(() => this.resetToDisconnected());
  }

  private resetToDisconnected(): void {
    this.connection = undefined;
    this.startPromise = undefined;
    this.connectionStateSignal.set(HubConnectionState.Disconnected);
  }

  private recordRecentEvent(event: NotificationEvent): void {
    // Newest first; drop the oldest once we hit the cap.
    const buffer = [event, ...this.recentEventsSubject.value].slice(0, MAX_RECENT_EVENTS);

    this.recentEventsSubject.next(buffer);
    persistRecentEvents(buffer);
  }
}

function loadRecentEvents(): NotificationEvent[] {
  try {
    const raw = sessionStorage.getItem(RECENT_EVENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as NotificationEvent[]).slice(0, MAX_RECENT_EVENTS) : [];
  } catch {
    return [];
  }
}

function persistRecentEvents(events: NotificationEvent[]): void {
  try {
    sessionStorage.setItem(RECENT_EVENTS_STORAGE_KEY, JSON.stringify(events));
  } catch (error) {
    // Quota exceeded or storage unavailable — best-effort, do not break the stream.
    console.warn('[NotificationsHubClient] Could not persist recent events:', error);
  }
}
